import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Activity, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const HealthScoreCircle = ({ score, interpretation, components }) => {
  const getColorClass = (color) => {
    const colors = {
      // New calm preventive palette
      emerald: 'text-emerald-700 border-emerald-400 bg-emerald-50',
      green:   'text-emerald-700 border-emerald-400 bg-emerald-50',
      amber:   'text-amber-700 border-amber-400 bg-amber-50',
      sky:     'text-sky-700 border-sky-400 bg-sky-50',
      // Legacy colors kept for backward compat
      blue:   'text-sky-700 border-sky-400 bg-sky-50',
      yellow: 'text-amber-700 border-amber-400 bg-amber-50',
      red:    'text-sky-700 border-sky-400 bg-sky-50',
    };
    return colors[color] || colors.green;
  };

  const getGradientClass = (color) => {
    const gradients = {
      emerald: 'from-emerald-400 to-teal-500',
      green:   'from-emerald-400 to-teal-500',
      amber:   'from-amber-400 to-orange-500',
      sky:     'from-sky-400 to-blue-500',
      blue:    'from-sky-400 to-indigo-500',
      yellow:  'from-amber-400 to-orange-500',
      red:     'from-sky-400 to-blue-500',
    };
    return gradients[color] || gradients.green;
  };

  const getImprovementTip = (category, score) => {
    if (score >= 80) {
      return "You're in a lovely rhythm. Stay gentle with yourself and let consistency do the rest.";
    }
    if (score >= 60) {
      return "Small steady steps are doing the work. Pick one habit (sleep, hydration, or a short walk) to lean into today.";
    }
    if (score >= 40) {
      return "A gentler day. Today, focus on one tiny win — a glass of water, a 10-minute walk, or going to bed a little earlier.";
    }
    return "Today asks for kindness, not pressure. Hydrate, rest, and check in again tomorrow — that's enough.";
  };

  const circumference = 2 * Math.PI * 70;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  return (
    <Card className={`border-2 ${getColorClass(interpretation?.color)}`}>
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row items-center gap-6">
          {/* Circular Score Display */}
          <div className="relative flex-shrink-0">
            <svg className="transform -rotate-90" width="180" height="180">
              {/* Background circle */}
              <circle
                cx="90"
                cy="90"
                r="70"
                stroke="currentColor"
                strokeWidth="12"
                fill="none"
                className="text-gray-200"
              />
              {/* Progress circle */}
              <circle
                cx="90"
                cy="90"
                r="70"
                stroke="url(#scoreGradient)"
                strokeWidth="12"
                fill="none"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                strokeLinecap="round"
                className="transition-all duration-1000 ease-out"
              />
              <defs>
                <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" className={`${getGradientClass(interpretation?.color)} stop-color`} />
                  <stop offset="100%" className={`${getGradientClass(interpretation?.color)} stop-color`} />
                </linearGradient>
              </defs>
            </svg>
            
            {/* Score Text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className={`text-4xl font-bold ${getColorClass(interpretation?.color)}`}>
                {score}
              </span>
              <span className="text-xs text-gray-500 mt-1">out of 100</span>
            </div>
          </div>

          {/* Score Interpretation */}
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
              <span className="text-3xl">{interpretation?.emoji}</span>
              <h3 className="text-2xl font-bold text-gray-900">
                {interpretation?.category}
              </h3>
            </div>
            
            {/* What This Means */}
            <div className="mb-4 p-3 bg-white/90 rounded-lg border border-gray-200">
              <p className="text-xs font-bold text-emerald-900 mb-1">A gentle note</p>
              <p className="text-sm text-gray-800 leading-relaxed">
                {interpretation?.message}
              </p>
            </div>

            {/* How to Improve */}
            <div className="mb-4 p-3 bg-gradient-to-r from-emerald-50 to-sky-50 rounded-lg border border-emerald-200">
              <p className="text-xs font-bold text-emerald-900 mb-1">One tiny next step</p>
              <p className="text-sm text-gray-800 leading-relaxed">
                {getImprovementTip(interpretation?.category, score)}
              </p>
            </div>

            {/* Component Breakdown */}
            {components && (
              <div className="grid grid-cols-2 gap-2 mt-4">
                <ScoreComponent label="Sleep" score={components.sleep_score} />
                <ScoreComponent label="Energy" score={components.energy_score} />
                <ScoreComponent label="Mood" score={components.mood_score} />
                <ScoreComponent label="Stress" score={components.stress_score} />
                <ScoreComponent label="Hydration" score={components.hydration_score} />
                <ScoreComponent label="Movement" score={components.activity_score} />
                {typeof components.nutrition_score === 'number' && (
                  <ScoreComponent label="Nutrition" score={components.nutrition_score} />
                )}
                {typeof components.consistency_bonus === 'number' && components.consistency_bonus > 0 && (
                  <ScoreComponent
                    label={`Streak +${components.consistency_bonus}`}
                    score={Math.min(100, 60 + (components.streak_days || 0) * 3)}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ScoreComponent = ({ label, score }) => {
  const getColor = (score) => {
    if (score >= 80) return 'bg-emerald-100 text-emerald-800 border-emerald-300';
    if (score >= 60) return 'bg-sky-100 text-sky-800 border-sky-300';
    if (score >= 40) return 'bg-amber-100 text-amber-800 border-amber-300';
    return 'bg-sky-50 text-sky-700 border-sky-200';
  };

  return (
    <div className={`px-2 py-1 rounded-lg border text-xs font-medium ${getColor(score)}`}>
      <div className="flex justify-between items-center">
        <span>{label}</span>
        <span className="font-bold">{score}</span>
      </div>
    </div>
  );
};

export default HealthScoreCircle;
